import React, { useState, useEffect, useCallback } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title as ChartTitle,
  Tooltip as ChartTooltip,
  Legend
} from "chart.js";
import { Bar } from "react-chartjs-2";

import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import TextField from "@material-ui/core/TextField";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Button from "@material-ui/core/Button";
import { toast } from "react-toastify";
import moment from "moment";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import api from "../../services/api";
import toastError from "../../errors/toastError";

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTitle, ChartTooltip, Legend);

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll", overflowX: "auto",
    ...theme.scrollbarStyles
  },
  filterRow: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    marginBottom: theme.spacing(2)
  },
  totalCard: {
    padding: theme.spacing(1.5),
    textAlign: "center"
  },
  chartContainer: {
    height: 300,
    position: "relative",
    marginBottom: theme.spacing(3)
  },
  priceInput: {
    width: 110
  }
}));

const formatUsd = value => `US$ ${Number(value || 0).toFixed(2)}`;

// Tela de custos de IA - so' admin (gate no menu lateral, ver
// MainListItems.js). Aba 1 mostra uso/custo agregado (grafico + tabela
// por servico/modelo); aba 2 e' a manutencao dos precos por modelo, que
// o usuario precisa conferir/atualizar (preco de IA muda com o tempo,
// sem API confiavel pra buscar automaticamente - ver seed da migration
// 20260910130100-create-aimodelpricing.ts).
const AiCosts = () => {
  const classes = useStyles();
  const [tab, setTab] = useState(0);

  const [dateFrom, setDateFrom] = useState(moment().subtract(30, "days").format("YYYY-MM-DD"));
  const [dateTo, setDateTo] = useState(moment().format("YYYY-MM-DD"));
  const [loading, setLoading] = useState(false);
  const [summaryData, setSummaryData] = useState({ totals: {}, byDay: [], byService: [] });

  const [pricing, setPricing] = useState([]);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [editedPricing, setEditedPricing] = useState({});

  const loadSummary = useCallback(async () => {
    if (!moment(dateFrom, "YYYY-MM-DD", true).isValid() || !moment(dateTo, "YYYY-MM-DD", true).isValid()) {
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/ai-usage/summary", { params: { dateFrom, dateTo } });
      setSummaryData(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  const loadPricing = useCallback(async () => {
    setPricingLoading(true);
    try {
      const { data } = await api.get("/ai-model-pricing");
      setPricing(data);
    } catch (err) {
      toastError(err);
    } finally {
      setPricingLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    if (tab === 1) loadPricing();
  }, [tab, loadPricing]);

  const handlePriceFieldChange = (id, field, value) => {
    setEditedPricing(prev => ({
      ...prev,
      [id]: { ...prev[id], [field]: value }
    }));
  };

  const handleSavePricing = async row => {
    const edited = editedPricing[row.id] || {};
    const inputPrice = edited.inputPricePerMillionTokensUsd ?? row.inputPricePerMillionTokensUsd;
    const outputPrice = edited.outputPricePerMillionTokensUsd ?? row.outputPricePerMillionTokensUsd;
    try {
      await api.put(`/ai-model-pricing/${row.id}`, {
        inputPricePerMillionTokensUsd: inputPrice,
        outputPricePerMillionTokensUsd: outputPrice
      });
      toast.success("Preço atualizado.");
      loadPricing();
    } catch (err) {
      toastError(err);
    }
  };

  const chartData = {
    labels: (summaryData.byDay || []).map(d => moment(d.day).format("DD/MM")),
    datasets: [
      {
        label: "Custo estimado (US$)",
        data: (summaryData.byDay || []).map(d => Number(d.estimatedCostUsd || 0)),
        backgroundColor: "rgba(59, 113, 202, 0.8)",
        borderRadius: 4,
        barThickness: 24
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: { y: { beginAtZero: true } }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Custos de IA</Title>
      </MainHeader>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} indicatorColor="primary" textColor="primary">
        <Tab label="Uso e custos" />
        <Tab label="Preços por modelo" />
      </Tabs>

      <div className={classes.mainPaper}>
        {tab === 0 && (
          <>
            <div className={classes.filterRow}>
              <TextField
                label="De"
                type="date"
                size="small"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Até"
                type="date"
                size="small"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </div>

            <Grid container spacing={2} style={{ marginBottom: 16 }}>
              <Grid item xs={4}>
                <Paper className={classes.totalCard} variant="outlined">
                  <Typography variant="h5" style={{ fontWeight: "bold" }}>
                    {formatUsd(summaryData.totals?.estimatedCostUsd)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Custo estimado no período
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper className={classes.totalCard} variant="outlined">
                  <Typography variant="h5" style={{ fontWeight: "bold" }}>
                    {Number(summaryData.totals?.totalTokens || 0).toLocaleString("pt-BR")}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Tokens totais
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper className={classes.totalCard} variant="outlined">
                  <Typography variant="h5" style={{ fontWeight: "bold" }}>
                    {(summaryData.byService || []).reduce((acc, s) => acc + Number(s.callCount || 0), 0)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Chamadas de IA
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            <div className={classes.chartContainer}>
              {(summaryData.byDay || []).length === 0 ? (
                <Typography color="textSecondary" align="center" style={{ paddingTop: 100 }}>
                  Nenhum uso de IA registrado nesse período.
                </Typography>
              ) : (
                <Bar data={chartData} options={chartOptions} />
              )}
            </div>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Serviço</TableCell>
                  <TableCell>Provedor</TableCell>
                  <TableCell>Modelo</TableCell>
                  <TableCell align="right">Chamadas</TableCell>
                  <TableCell align="right">Tokens</TableCell>
                  <TableCell align="right">Custo estimado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRowSkeleton columns={6} />
                ) : (
                  (summaryData.byService || []).map((row, i) => (
                    <TableRow key={i}>
                      <TableCell>{row.service}</TableCell>
                      <TableCell>{row.provider}</TableCell>
                      <TableCell>{row.model}</TableCell>
                      <TableCell align="right">{row.callCount}</TableCell>
                      <TableCell align="right">{Number(row.totalTokens || 0).toLocaleString("pt-BR")}</TableCell>
                      <TableCell align="right">{formatUsd(row.estimatedCostUsd)}</TableCell>
                    </TableRow>
                  ))
                )}
                {!loading && (summaryData.byService || []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Sem dados no período.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </>
        )}

        {tab === 1 && (
          <>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
              Preço de IA muda com frequência — confira e atualize os valores abaixo periodicamente. O custo
              estimado na aba "Uso e custos" usa sempre o preço ATUAL cadastrado aqui, não um preço histórico.
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Provedor</TableCell>
                  <TableCell>Modelo</TableCell>
                  <TableCell align="right">US$ / milhão tokens (entrada)</TableCell>
                  <TableCell align="right">US$ / milhão tokens (saída)</TableCell>
                  <TableCell align="center">Ação</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pricingLoading ? (
                  <TableRowSkeleton columns={5} />
                ) : (
                  pricing.map(row => (
                    <TableRow key={row.id}>
                      <TableCell>{row.provider}</TableCell>
                      <TableCell>{row.model}</TableCell>
                      <TableCell align="right">
                        <TextField
                          className={classes.priceInput}
                          type="number"
                          size="small"
                          inputProps={{ step: "0.01" }}
                          defaultValue={row.inputPricePerMillionTokensUsd}
                          onChange={e =>
                            handlePriceFieldChange(row.id, "inputPricePerMillionTokensUsd", e.target.value)
                          }
                        />
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          className={classes.priceInput}
                          type="number"
                          size="small"
                          inputProps={{ step: "0.01" }}
                          defaultValue={row.outputPricePerMillionTokensUsd}
                          onChange={e =>
                            handlePriceFieldChange(row.id, "outputPricePerMillionTokensUsd", e.target.value)
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Button size="small" color="primary" onClick={() => handleSavePricing(row)}>
                          Salvar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </>
        )}
      </div>
    </MainContainer>
  );
};

export default AiCosts;
